import React, { useEffect, useRef, useState } from 'react';
import { SmartTagsTextInputLayoutProps, Tag } from 'ui-builder';

import styles from './SmartTagsTextInput.module.scss';

export default function SmartTagsTextInputLayout(props: SmartTagsTextInputLayoutProps) {
  const [visibleTags, setVisibleTags] = useState<Tag[]>([]);

  const [inputLeftOffset, setInputLeftOffset] = useState<number>();

  const ref = useRef<HTMLDivElement>(null);

  const [selectionListLeftOffset, setSelectionListLeftOffset] = useState<number>();

  useEffect(() => {
    // Set the initial value when the component mounts
    if (ref.current && props.value) {
      ref.current.innerText = props.value;
    }
  }, []);

  function hideSuggestions() {
    setVisibleTags([]);
    setSelectionListLeftOffset(undefined);
  }


  function getCursorCoordinates() {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) {
      return {
        x: 0,
        y: 0
      };
    }

    const range = selection.getRangeAt(0)
      .cloneRange();
    const span = document.createElement('span');
    // Ensure the span has content so it takes up space
    span.textContent = '\u200b'; // Zero-width space
    range.insertNode(span);
    const {
      offsetTop,
      offsetLeft
    } = span;

    const coordinates = {
      x: offsetLeft,
      y: offsetTop
    };
    span.remove();
    return coordinates;
  }

  function selectSuggestion(tag: Tag) {
    const selection = window.getSelection();

    if (!selection || !selection.rangeCount) return;

    const value = selection.anchorNode?.textContent || '';

    const valueBeforeCursor = value.substring(0, selection?.anchorOffset || 0);
    const sharpIndex = valueBeforeCursor.lastIndexOf('#');

    const range = document.createRange();
    range.setStart(selection.anchorNode!, sharpIndex);
    range.setEnd(selection.anchorNode!, selection.anchorOffset);

    range.deleteContents(); // Delete the typed '#' and any selected text

    const tagElement = document.createElement('span');
    tagElement.setAttribute('contenteditable', 'false');
    tagElement.classList.add(styles.insertedTag);
    tagElement.innerText = '#' + tag.value;

    range.insertNode(tagElement);
    range.insertNode(document.createTextNode(' '));

    // Move the cursor after the inserted node
    range.setStartAfter(tagElement);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    hideSuggestions();
    // Trigger the onChange callback manually
    props.onChangeCallback({ target: { value: ref.current!.innerText } });
  }

  const onInput = () => {
    const selection = window.getSelection();

    const value = selection!.anchorNode?.textContent || '';

    const valueBeforeCursor = value.substring(0, selection?.anchorOffset || 0);

    const sharpIndex = valueBeforeCursor.lastIndexOf('#');

    if (sharpIndex > -1) {
      const query = valueBeforeCursor.substring(sharpIndex + 1);

      if (query.length > 2) {
        setVisibleTags(
          props.availableTags.filter((tag) => tag.value.toLowerCase()
            .includes(query.toLowerCase()))
        );

        if (selectionListLeftOffset === undefined) {
          const coordinates = getCursorCoordinates();
          setSelectionListLeftOffset(coordinates.x - (inputLeftOffset || 0));
        }
      } else {
        hideSuggestions();
      }
    } else {
      hideSuggestions();
    }

    // Since onInput is handling content changes, ensure we're calling props.onChangeCallback here
    props.onChangeCallback({ target: { value } });
  };

  useEffect(() => {
    if (ref.current) {
      setInputLeftOffset(ref.current.offsetLeft + 30);
    }
  }, [ref]);

  return (
    <div>
      <div
        className="block-like-input single-line"
        contentEditable={true}
        onInput={onInput}
        ref={ref}
      ></div>
      <ul
        style={{
          display: visibleTags.length > 0 ? 'block' : 'none',
          marginLeft: selectionListLeftOffset,
        }}
        className={styles.suggestionsList}
      >
        {visibleTags.map((tag) => (
          <li
            key={tag.id}
            onClick={() => selectSuggestion(tag)}
            onMouseDown={(event) => {
              event.preventDefault();
            }}
          >
            {tag.value}
          </li>
        ))}
      </ul>
    </div>
  );
}
